import { AnyAction, Dispatch } from 'redux';
import { routes } from '../../../routing/routes';
import { TireServicesSelectionType } from '../../../screens/FormPage/Step3/utils';
import { notesFiltersInitialValues } from '../../../screens/NotesPage/Filters';
import { resolveKeys } from '../../../screens/utils';
import { getOptionsListEnum, isNotesEnabled } from '../../../services/common/miscUtils';
import { setIsCarUsed, setSelectedVehicleType } from '../../../services/redux/features/custom.slice';
import { setFilters } from '../../../services/redux/features/notes.slice';
/* eslint-disable import/extensions */
import dashboard from '../content/dashboard.json';
import { BooleanEnum, DealerDashboardSubpage } from './enums';
import { VehicleType } from './vehicleTypes';

export type ListOfEnumValues<T extends string> = Array<{ value: T; label: string }>;
export type ListOfNotUniqueEnumValues<T extends string> = Array<{ value: T; label: string; id: string }>;
export type ListOfEnumValuesWithEmpty<T extends string> = Array<{ value: '' | T; label: string }>;

export const genderOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'FEMALE', label: 'Madame' },
  { value: 'MALE', label: 'Monsieur' },
  { value: 'OTHER', label: 'Autre' },
] satisfies ListOfEnumValuesWithEmpty<CustomerIntegration.Schemas.GenderType>;

export const salutationOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'MR', label: 'Monsieur' },
  { value: 'MRS', label: 'Madame' },
  { value: 'MISS', label: 'Mademoiselle' },
  { value: 'DIVERSE', label: 'Autre' },
] satisfies ListOfEnumValuesWithEmpty<CustomerIntegration.Schemas.SalutationType>;

export const SalutationEnum = getOptionsListEnum(salutationOptions);

export const LVDLDropdown = [
  { value: 'Employee', label: 'Employé' },
  { value: 'Beneficiary', label: 'Ayant-droit' },
] as const;

export const LVDLDropdownEnum = getOptionsListEnum(LVDLDropdown);

export const yesOrNot = [
  { value: '', label: 'Sélectionner' },
  { value: true, label: 'Oui' },
  { value: false, label: 'Non' },
] as const;

export const emptyArray = [
  { value: '', label: 'Sélectionner' },
  { value: 'TestLegalEntity', label: 'TestLegalEntity' },
] as const;

export const isExistingClient = [
  { value: '', label: 'Sélectionner' },
  { value: BooleanEnum.false, label: 'Nouveau Client' },
  { value: BooleanEnum.true, label: 'Déjà Client' },
] as const;

export const contractTypeOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'INTERIM_CONTRACT', label: `Contrat intérimaire` },
  {
    value: 'APPRENTICE',
    label: `Contrat d'apprentissage/ professionnalisation`,
  },
  { value: 'SEASONAL', label: `Contrat saisonnier` },
  { value: 'NO_CONTRACT', label: `Sans contrat de travail` },
  { value: 'PERMANENT', label: `Contrat à durée indéterminée` },
  { value: 'FIXED', label: `Contrat à durée déterminée` },
  { value: 'PERMANENT_ON_TRIAL', label: `CDI en periode d'essai` },
] satisfies ListOfEnumValuesWithEmpty<CreditIntegration.Schemas.EmploymentContractType>;

export const ContractTypeEnum = getOptionsListEnum(contractTypeOptions);

export const housingSituationOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'PROPERTY_ACCESS', label: 'Accédant à la propriété' },
  { value: 'HOSTED_FREE', label: 'Hébergé à titre gratuit' },
  { value: 'RENT', label: 'Locataire' },
  { value: 'HOUSED_ADMIN', label: "Logé par l'administration" },
  { value: 'HOUSED_EMPLOYER', label: "Logé par l'employeur" },
  { value: 'HOUSED_PARENTS', label: 'Parents' },
  { value: 'PROPERTY_OWNERSHIP', label: 'Propriétaire' },
] satisfies ListOfEnumValuesWithEmpty<CreditIntegration.Schemas.HousingSituation>;

export const maritalStatusOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'MARRIED', label: 'Marié' },
  { value: 'SINGLE', label: 'Célibataire' },
  { value: 'SEPARATED', label: 'Séparé' },
  { value: 'DIVORCED', label: 'Divorcé' },
  { value: 'WIDOWED', label: 'Veuf' },
  { value: 'LIVING_TOGETHER', label: 'Concubin' },
  { value: 'CIVIL_PARTNERSHIP', label: 'Pacsé' },
] satisfies ListOfEnumValuesWithEmpty<CreditIntegration.Schemas.MaritalStatusType>;

export const kindOfCompany = [
  { value: '', label: 'Sélectionner' },
  { value: 'MANAGING_DIRECTOR', label: 'Gérant' },
  { value: 'ADMINISTRATOR', label: 'Administrateur' },
  {
    value: 'BENEFICIAL_OWNER',
    label: 'Participation > 25% (Bénéficiaire effectif)',
  },
] satisfies ListOfEnumValuesWithEmpty<CreditIntegration.Schemas.InvolvedInCompanyCategoryType>;

export const entityLegalFormOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'ASSO', label: 'Association/Organisme/Fondation' },
  { value: 'INDIV_PM', label: 'Indivision (pers. Morale)' },
  { value: 'ETR', label: 'Société de droit étranger' },
  { value: 'GIE', label: "Groupement d'intérêt économique" },
  { value: 'PUBLIC', label: 'Organisme/Etablissement public' },
  { value: 'SARL', label: 'Société à responsabilité limitée (SARL)' },
  { value: 'SA', label: 'Société Anonyme' },
  { value: 'SCI', label: 'Société civile' },
  { value: 'SCA', label: 'Société coopérative agricole' },
  { value: 'SC', label: 'Société en commandite' },
  { value: 'SNC', label: 'Société en nom collectif' },
  { value: 'SAS', label: 'Société par actions simplifiée' },
  { value: 'AUT', label: 'Autre' },
  { value: 'EI', label: 'Entrepreneur individuel' },
  { value: 'INDIV_PP', label: 'Indivision (pers. Physique)' },
] satisfies ListOfEnumValuesWithEmpty<QuoteSchema.Schemas.EntityLegalFormType>;

export const milesCoCustomerRelationOptions = [
  { value: '', label: 'Sélectionner' },
  { value: 'MARRIED', label: 'Marié(e)' },
  { value: 'PACSED', label: 'Pacsé(e)' },
  { value: 'COHABITATING', label: 'Concubin(e)' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'OTHER', label: 'Autre relation' },
] satisfies ListOfEnumValuesWithEmpty<QuoteSchema.Schemas.MilesCoCustomerRelationType>;

export const modelList = [
  { value: '', label: 'Sélectionner' },
  { value: '2022', label: '2022' },
  { value: '2021', label: '2021' },
  { value: '2020', label: '2020' },
] as const;

export const engineList = [
  { value: 'Diesel', label: 'Diesel' },
  { value: 'Essence', label: 'Essence' },
  { value: 'Hybride', label: 'Hybride' },
  { value: 'Electrique', label: 'Electrique' },
  { value: 'HENR', label: 'Hybrid Essence non rechargeable' },
] as const;

export const tireServicesSelectionList = [
  { value: '', label: 'Sélectionner' },
  { value: TireServicesSelectionType.SUMMER_TIRES, label: 'Pneus été' },
  { value: TireServicesSelectionType.WINTER_TIRES, label: 'Pneus hiver' },
  { value: TireServicesSelectionType.SUMMER_AND_WINTER_TIRES, label: 'Pneus été & Pneus hiver' },
  // TODO: Uncomment once supported by backoffice
  // Commented-out temporarily together with backoffice adjustment as per FRPOS-3482
  // { value: TireServicesSelectionType.ALL_SEASON_TIRES, label: 'Pneus 4 saisons' },
] satisfies Array<{ value: TireServicesSelectionType | ''; label: string }>;

export const tireCountOptionsList = [
  { value: '2', label: '2' },
  { value: '4', label: '4' },
  { value: '6', label: '6' },
  { value: '8', label: '8' },
  { value: '10', label: '10' },
  { value: '12', label: '12' },
  { value: '14', label: '14' },
  { value: '16', label: '16' },
  { value: '18', label: '18' },
  { value: '20', label: '20' },
] as const;

export const telephoneCountryCodesOptions = [
  { value: '+33', label: 'FR +33' },
  { value: '+43', label: 'AT +43' },
  { value: '+32', label: 'BE +32' },
  { value: '+359', label: 'BG +359' },
  { value: '+385', label: 'HR +385' },
  { value: '+420', label: 'CZ +420' },
  { value: '+45', label: 'DK +45' },
  { value: '+372', label: 'EE +372' },
  { value: '+358', label: 'FI +358' },
  { value: '+49', label: 'DE +49' },
  { value: '+30', label: 'GR +30' },
  { value: '+36', label: 'HU +36' },
  { value: '+354', label: 'IS +354' },
  { value: '+39', label: 'IT +39' },
  { value: '+371', label: 'LV +371' },
  { value: '+423', label: 'LI +423' },
  { value: '+370', label: 'LT +370' },
  { value: '+352', label: 'LU +352' },
  { value: '+356', label: 'MT +356' },
  { value: '+31', label: 'NL +31' },
  { value: '+47', label: 'NO +47' },
  { value: '+48', label: 'PL +48' },
  { value: '+351', label: 'PT +351' },
  { value: '+40', label: 'RO +40' },
  { value: '+421', label: 'SK +421' },
  { value: '+386', label: 'SI +386' },
  { value: '+34', label: 'ES +34' },
  { value: '+46', label: 'SE +46' },
  { value: '+41', label: 'CH +41' },
] satisfies ListOfEnumValues<CustomerIntegration.Schemas.PhoneCountryCode>;

export const countryToMobilePhoneDigitsMapping = {
  '+49': { country: 'Allemagne', digits: [10, 11] },
  '+43': { country: 'Autriche', digits: [10] },
  '+32': { country: 'Belgique', digits: [9] },
  '+359': { country: 'Bulgarie', digits: [8, 9] },
  '+385': { country: 'Croatie', digits: [9] },
  '+357': { country: 'Chypre', digits: [8] },
  '+45': { country: 'Danemark', digits: [8] },
  '+34': { country: 'Espagne', digits: [9] },
  '+372': { country: 'Estonie', digits: [8] },
  '+358': { country: 'Finlande', digits: [9, 10] },
  '+33': { country: 'France', digits: [9, 10] },
  '+30': { country: 'Grèce', digits: [10] },
  '+36': { country: 'Hongrie', digits: [9] },
  '+354': { country: 'Islande', digits: [7, 9] },
  '+39': { country: 'Italie', digits: [10] },
  '+371': { country: 'Lettonie', digits: [8] },
  '+423': { country: 'Liechtenstein', digits: [7, 9] },
  '+370': { country: 'Lituanie', digits: [8] },
  '+352': { country: 'Luxembourg', digits: [9] },
  '+356': { country: 'Malte', digits: [8] },
  '+47': { country: 'Norvège', digits: [8] },
  '+31': { country: 'Pays-Bas', digits: [9] },
  '+48': { country: 'Pologne', digits: [9] },
  '+351': { country: 'Portugal', digits: [9] },
  '+420': { country: 'République Tchèque', digits: [9] },
  '+40': { country: 'Roumanie', digits: [9] },
  '+421': { country: 'Slovaquie', digits: [9] },
  '+386': { country: 'Slovénie', digits: [8] },
  '+46': { country: 'Suède', digits: [9] },
  '+41': { country: 'Suisse', digits: [9] },
  '+44': { country: 'Royaume-Uni', digits: [10, 11] },
} as const;

export const TelephoneCountryCodesEnum = getOptionsListEnum(telephoneCountryCodesOptions);

export const professionOptions = [
  { value: '', label: 'Sélectionner' },
  { value: '44_CLERGE_RELIGIEUX', label: `CLERGE RELIGIEUX` },
  { value: '99_PROFESSION_LIBERALE', label: `PROFESSION LIBERALE` },
  { value: '84_ELEVES_ETUDIANTS', label: `ELEVES ETUDIANTS` },
  { value: '85_PERS_SANS_ACTIVITE_PROF_LESS_60', label: `PERS.SANS ACTIVITE PROF < 60` },
  { value: '86_PERS_SANS_ACTIVITE_PROF_GREATER_60', label: `PERS.SANS ACTIVITE PROF > 60` },
  { value: '96_INVALIDE', label: `INVALIDE` },
  { value: '33_CADRES_FONCTION_PUBLIQUE', label: `CADRES FONCTION PUBLIQUE` },
  { value: '34_PROFESSEURS_PROFESSIONS_SCIENT', label: `PROFESSEURS,PROFESSIONS  SCIENT` },
  {
    value: '35_PROF_INFORMATION_ARTS_SPECTAC',
    label: `PROF.  INFORMATION,ARTS,SPECTAC`,
  },
  { value: '37_CADRES_ADMIN_ET_COM_D_ENTREPRI', label: `CADRES ADMIN ET COM D'ENTREPRI` },
  { value: '38_INGENIEURS_ET_CADRES_TECHNIQUE', label: `INGENIEURS ET CADRES  TECHNIQUE` },
  { value: '42_INSTITUTEURS_ET_ASSIMILES', label: `INSTITUTEURS ET ASSIMILES` },
  { value: '43_PROF_INTERMEDIAIRE_DE_LA_SANTE', label: `PROF.INTERMEDIAIRE DE LA SANTE` },
  { value: '45_PROF_INTER_ADM_FONCTION_PUBLIQ', label: `PROF.INTER.ADM.FONCTION PUBLIQ` },
  { value: '46_PROF_INTER_ADM_CIALE_ENTREPRIS', label: `PROF.INTER.ADM.CIALE ENTREPRIS` },
  { value: '47_TECHNICIENS', label: `TECHNICIENS` },
  { value: '48_CONTREMAITRES_AGENTS_MAITRISE', label: `CONTREMAITRES AGENTS MAITRISE` },
  { value: '52_EMP_CIVIL_AGTS_SERV_FONC_PUBLI', label: `EMP.CIVIL.AGTS SERV FONC PUBLI` },
  { value: '53_POLICIERS_ET_MILITAIRES', label: `POLICIERS ET MILITAIRES` },
  {
    value: '54_EMPLOYES_ADMINISTRATIFS_ENTREP',
    label: `EMPLOYES ADMINISTRATIFS  ENTREP`,
  },
  { value: '55_EMPLOYES_DE_COMMERCE', label: `EMPLOYES DE COMMERCE` },
  {
    value: '56_PERS_SERV_DIRECTS_AUX_PARTICUL',
    label: `PERS.SERV DIRECTS AUX PARTICUL`,
  },
  { value: '61_OUVRIERS_QUALIFIES', label: `OUVRIERS QUALIFIES` },
  { value: '64_CHAUFFEURS', label: `CHAUFFEURS` },
  { value: '66_OUVRIERS_NON_QUALIFIES', label: `OUVRIERS NON QUALIFIES` },
  { value: '69_OUVRIERS_AGRICOLES', label: `OUVRIERS AGRICOLES` },
  { value: '80_CHOMEURS', label: `CHOMEURS` },
  { value: '90_EMPLOI_PRECAIRE', label: `EMPLOI PRECAIRE` },
  { value: '93_AGRICULTEUR', label: `AGRICULTEUR` },
  { value: '94_ENTREPRISE_INDIVIDUELLE', label: `ENTREPRISE INDIVIDUELLE` },
  { value: '97_DIRIGEANT_DE_SOCIETE', label: `DIRIGEANT DE SOCIETE` },
  { value: '98_AUTO_ENTREPRENEUR', label: `AUTO-ENTREPRENEUR` },
  { value: '70_RETRAITES', label: `RETRAITES` },
] satisfies ListOfEnumValuesWithEmpty<CustomerIntegration.Schemas.OccupationEnumValue>;

export const ProfessionEnum = getOptionsListEnum(professionOptions);

export const soleTraderProfessionOptions = [
  { value: '', label: 'Sélectionner' },
  { value: '99_PROFESSION_LIBERALE', label: `PROFESSION LIBÉRALE` },
  { value: '93_AGRICULTEUR', label: `AGRICULTEUR` },
  { value: '94_ENTREPRISE_INDIVIDUELLE', label: `ARTISAN/COMMERÇANT` },
] satisfies ListOfEnumValuesWithEmpty<CustomerIntegration.Schemas.OccupationEnumValue>;

export const modelGroup = [
  { value: 'modelA', label: 'Model A' },
  { value: 'modelB', label: 'Model B' },
] as const;

export const genreList = [
  { value: 'DVP', label: 'Dérivé VP' },
  { value: 'VP', label: 'Véhicule particulier' },
  { value: 'VU', label: 'Véhicule utilitaire' },
] as const;

export const damagesWaiver = [
  { value: '0', label: '0' },
  { value: '250', label: '250' },
  { value: '500', label: '500' },
  { value: '750', label: '750' },
  { value: '1000', label: '1000' },
  { value: '1250', label: '1250' },
  { value: '1500', label: '1500' },
] satisfies Array<{ value: QuoteSchema.Schemas.MilesDamageWaiverType; label: string }>;

export const MilesDamageWaiverTypeEnum = getOptionsListEnum(damagesWaiver);

export const dealerIDlist = [
  {
    value: '01007490',
    label: '01007490/VOLKSWAGEN',
    brand: 'Volkswagen',
    index: 1,
  },
  { value: '01002935', label: '01002935/AUDI', brand: 'Audi', index: 2 },
  { value: '15000622', label: '15000622/SKODA', brand: 'Skoda', index: 3 },
  { value: '15000630', label: '15000630/SKODA', brand: 'Skoda', index: 4 },
  { value: '15000632', label: '15000632/SKODA', brand: 'Skoda', index: 5 },
  { value: '15000638', label: '15000638/SKODA', brand: 'Skoda', index: 6 },
] as const;

export const dashboardTabs = {
  [DealerDashboardSubpage.Summary]: {
    label: 'Propositions',
    link: routes.dashboardQuotes,
  },
  [DealerDashboardSubpage.Studies]: {
    label: 'Etudes',
    link: routes.dashboardStudies,
  },
  [DealerDashboardSubpage.Accepted]: {
    label: 'Acceptés',
    link: routes.dashboardAccepted,
  },
  [DealerDashboardSubpage.Run]: {
    label: 'Contrats',
    link: routes.dashboardRun,
  },
  ...(isNotesEnabled() && {
    [DealerDashboardSubpage.Conversations]: {
      label: 'Conversations',
      link: routes.dashboardConversations,
    },
  }),
} as const;

export const subpagesNavigationList = [
  {
    value: 'Proposition',
    label: 'Proposition',
    link: routes.dashboardQuotes,
    index: 1,
    name: 'dashboardSubpageQuotes',
    descriptionList: resolveKeys(`descriptionListQuotationSummary`, dashboard),
  },
  {
    value: 'Etudes',
    label: 'Etudes',
    link: routes.dashboardStudies,
    index: 2,
    name: 'dashboardSubpageStudies',
    descriptionList: resolveKeys(`descriptionListStudies`, dashboard),
  },
  {
    value: 'Acceptés',
    label: 'Acceptés',
    link: routes.dashboardAccepted,
    index: 3,
    name: 'dashboardSubpageAccepted',
    descriptionList: resolveKeys(`descriptionListAccepted`, dashboard),
  },
  {
    value: 'Contrats',
    label: 'Contrats',
    link: routes.dashboardRun,
    index: 4,
    name: 'dashboardSubpageRun',
    descriptionList: resolveKeys(`descriptionListRun`, dashboard),
  },
  ...(isNotesEnabled()
    ? [
        {
          value: 'Conversations',
          label: 'Conversations',
          link: routes.dashboardConversations,
          index: 5,
          name: 'dashboardSubpageConversations',
          descriptionList: resolveKeys(`descriptionListConversations`, dashboard),
        },
      ]
    : []),
] as const;

export const dashboardHomeNavigationListNew = [
  {
    value: 'Nouvelle offre',
    label: 'Nouvelle offre',
    link: routes.selectCarType,
    index: 1,
    name: 'createQuotation',
    icon: 'document-car',
    descriptionList: resolveKeys(`createQuotation`, dashboard),
    subNavigations: [
      {
        value: 'Véhicule Neuf',
        label: 'Véhicule Neuf',
        link: routes.configurator,
        index: 1,
        name: 'newQuote',
        onClick: (dispatch: Dispatch<AnyAction>) => {
          dispatch(setSelectedVehicleType(VehicleType.NEW));
          dispatch(setIsCarUsed(false));
        },
      },
      {
        value: 'Véhicule Stock',
        label: 'Véhicule Stock',
        link: routes.vehicleConfigurationPage,
        index: 2,
        name: 'stockQuote',
        onClick: (dispatch: Dispatch<AnyAction>) => {
          dispatch(setSelectedVehicleType(VehicleType.STOCK));
          dispatch(setIsCarUsed(false));
        },
      },
      {
        value: 'Véhicule d’occasion',
        label: 'Véhicule d’occasion',
        link: routes.vehicleConfigurationPage,
        index: 3,
        name: 'usedQuote',
        onClick: (dispatch: Dispatch<AnyAction>) => {
          dispatch(setSelectedVehicleType(VehicleType.USED));
          dispatch(setIsCarUsed(true));
        },
      },
    ],
  },
  {
    value: 'Activity Tracking',
    label: "Suivi d'activité",
    link: routes.dashboardQuotesActive,
    index: 2,
    icon: 'semantic-performance',
    name: 'createQuotation',
    descriptionList: resolveKeys(`createQuotation`, dashboard),
    subNavigations: [
      {
        value: 'Proposition',
        label: 'Proposition',
        link: routes.dashboardQuotes,
        index: 3,
        name: 'dashboardSubpageQuotes',
        descriptionList: resolveKeys(`descriptionListQuotationSummary`, dashboard),
      },
      {
        value: 'Etudes',
        label: 'Etudes',
        link: routes.dashboardStudies,
        index: 4,
        name: 'dashboardSubpageStudies',
        descriptionList: resolveKeys(`descriptionListStudies`, dashboard),
      },
      {
        value: 'Acceptés',
        label: 'Acceptés',
        link: routes.dashboardAccepted,
        index: 5,
        name: 'dashboardSubpageAccepted',
        descriptionList: resolveKeys(`descriptionListAccepted`, dashboard),
      },
      {
        value: 'Contrats',
        label: 'Contrats',
        link: routes.dashboardRun,
        index: 6,
        name: 'dashboardSubpageRun',
        descriptionList: resolveKeys(`descriptionListRun`, dashboard),
      },
    ],
  },
  ...(isNotesEnabled()
    ? [
        {
          value: 'Conversations',
          label: 'Conversations',
          link: routes.dashboardConversations,
          index: 8,
          icon: 'semantic-contact',
          name: 'dashboardSubpageConversations',
          descriptionList: resolveKeys(`descriptionListConversations`, dashboard),
          subNavigations: [
            {
              value: 'Nouvelles conversation',
              label: 'Nouvelle',
              link: routes.dashboardConversations,
              index: 9,
              name: 'dashboardSubpageConversationsUnread',
              onClick: (dispatch: Dispatch<AnyAction>) =>
                dispatch(setFilters({ ...notesFiltersInitialValues, sortBy: 'unread' })),
            },
            {
              value: 'Déjà vues',
              label: 'Déjà vues',
              link: routes.dashboardConversations,
              index: 10,
              name: 'dashboardSubpageConversationsRead',
              onClick: (dispatch: Dispatch<AnyAction>) =>
                dispatch(setFilters({ ...notesFiltersInitialValues, sortBy: 'read' })),
            },
          ],
        },
      ]
    : []),
  {
    value: 'Avis de virement',
    label: 'Avis de virement',
    link: routes.dashboardDealerDocuments,
    index: 9,
    name: 'TransferNotice',
    icon: 'piggybank',
    descriptionList: resolveKeys(`createQuotation`, dashboard),
  },
] as const;

export const milesPackageListOptions = [
  { value: 'PACKAGE_S', label: 'S' },
  { value: 'PACKAGE_M', label: 'M' },
  { value: 'PACKAGE_L', label: 'L' },
  { value: 'PACKAGE_XL', label: 'XL' },
] satisfies ListOfEnumValues<QuoteSchema.Schemas.MilesPackageEnumType>;

export const milesPricingStrategyListOptions = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'BOOSTER', label: 'Booster' },
  { value: 'PROMOTION', label: 'Promotion' },
] satisfies ListOfEnumValues<QuoteSchema.Schemas.MilesPricingStrategyEnumType>;

export const MilesPricingStrategyEnum = getOptionsListEnum(milesPricingStrategyListOptions);

export const warrantyExtensionNewCarDurationOptons = [
  { value: '700013', label: '24 mois (constructeur)' },
  { value: '700014', label: '36 mois (2+1)' },
  { value: '700015', label: '48 mois (2+2)' },
] as const;

export const warrantyExtensionOldCarDurationOptons = [
  { value: '700016', label: '24 mois' },
  { value: '700017', label: '12 mois' },
  { value: '700018', label: '6 mois' },
  { value: '700019', label: '3 mois' },
  { value: '700020', label: 'Premier jour' },
  { value: '700021', label: `Jusqu'aux 5 ans du véhicule` },
] as const;

export const warrantyExtensionTypeOptions = [
  { value: '700012', label: 'Garantie Distributeur' },
  { value: '700011', label: 'Garantie VN' },
] as const;

export type WarrantyExtensionTypeType = (typeof warrantyExtensionTypeOptions)[number]['value'];

export const WarrantyExtensionTypeEnum = getOptionsListEnum(warrantyExtensionTypeOptions);
